import BackgroundHue from '@atoms/BackgroundHue/BackgroundHue';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import MotionWrapper from '@atoms/MotionWrapper/MotionWrapper';
import PriceDisplayText from '@atoms/PriceDisplayText/PriceDisplayText';
import { easeInOut } from '@config/animations.config';
import GlowColourEnum from '@models/enums/GlowColourEnum';
import type { ITabbedProductCardsCard } from '@models/ITabbedProductCards';
import updateDataLayer from '@utils/helpers/gtm';
import mapToNewTrackingData from '@utils/helpers/mapToNewTrackingData';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { useEffect, useRef, useState } from 'react';

const TabbedProductCard = ({
  badge,
  ctaLabel,
  fromPriceLabel,
  keyStats,
  productName,
  pricingData,
  thumbnail,
  trackingData,
  url,
}: ITabbedProductCardsCard) => {
  const [isHovered, setHover] = useState(false);
  const isBelowXLarge = useMediaQuery(1440);
  const [ctaBoxHeight, setCtaBoxHeight] = useState(0);
  const ctaBox = useRef<HTMLDivElement>(null);

  const handleSelectItemEvent = () => {
    if (!trackingData) return;

    updateDataLayer({
      event: 'selectItem',
      ecommerce: {
        currency: trackingData.currency,
        items: [mapToNewTrackingData(trackingData)],
      },
    });
  };

  useEffect(() => {
    setCtaBoxHeight(ctaBox.current?.clientHeight || 0);
  }, []);

  const containerVariants = {
    hover: { y: -ctaBoxHeight },
    initial: { y: 0 },
  };

  const ctaVariants = {
    hover: { opacity: 1 },
    initial: { opacity: isBelowXLarge ? 1 : 0 },
  };

  const imgVariants = {
    hover: { y: -15, scale: 0.9 },
    initial: { y: 0, scale: 1 },
  };

  const hueVariants = {
    hover: { opacity: 0.2 },
    initial: { opacity: 0 },
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Supress lint error to allow for events to be added to the container div.
    <div
      className="flex h-full flex-col gap-5 overflow-hidden rounded-[10px] bg-grey-600 p-5 md:gap-7.5 md:py-7.5 xl:gap-12"
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <MotionWrapper
        initial="initial"
        animate={isHovered && !isBelowXLarge ? 'hover' : 'initial'}
        variants={imgVariants}
        transition={easeInOut}
        className="relative flex h-[144px] w-full shrink-0 items-center justify-center rounded-[10px] md:h-[165px] lg:h-[220px] xl:h-[250px] 2xl:h-[300px]"
      >
        <a
          className="flex h-full w-full"
          href={url}
          target="_blank"
          onClick={handleSelectItemEvent}
        >
          <Image
            classes="z-10 w-full h-full"
            imageClasses="w-full h-full object-contain"
            sizes="(max-width: 768px) 376px, 500px"
            image={thumbnail}
          />

          <MotionWrapper
            initial="initial"
            animate={isHovered && !isBelowXLarge ? 'hover' : 'initial'}
            variants={hueVariants}
            className="pointer-events-none absolute top-10 left-0 z-0 flex h-full scale-150 items-center justify-center"
          >
            <BackgroundHue
              colour={GlowColourEnum.Yellow}
              className="w-full object-contain"
            />
          </MotionWrapper>
        </a>
      </MotionWrapper>

      <MotionWrapper
        initial="initial"
        animate={isHovered && !isBelowXLarge ? 'hover' : 'initial'}
        variants={containerVariants}
        transition={easeInOut}
        className="relative flex grow flex-col gap-4 md:gap-6"
      >
        <p className="h4">{productName}</p>

        {keyStats && keyStats?.length > 0 && (
          <ul className="body-two flex flex-col gap-4 text-grey">
            {keyStats?.map((stat) => (
              <li key={stat}>{stat}</li>
            ))}
          </ul>
        )}

        <div className="body-one mt-auto flex items-end justify-between gap-2 md:items-start">
          {pricingData?.formattedCalculatedPrice && (
            <PriceDisplayText
              pricingData={pricingData}
              fromPriceLabel={fromPriceLabel}
              containerClassName="body-one"
              containerFontSize="body-one"
              taxesClassName="hidden"
            />
          )}

          {badge && (
            <Image
              classes="h-6 w-6 shrink-0 md:h-8 md:w-8"
              imageClasses="h-full object-cover"
              image={badge}
            />
          )}
        </div>

        <MotionWrapper
          initial="initial"
          animate={isHovered && !isBelowXLarge ? 'hover' : 'initial'}
          variants={ctaVariants}
          transition={{ duration: 0.3 }}
          ref={ctaBox}
          className="flex w-fit pt-4 md:pt-6 xl:absolute xl:top-full"
        >
          <Button
            className="w-fit"
            link={{ name: ctaLabel, url }}
            linkCallback={handleSelectItemEvent}
          />
        </MotionWrapper>
      </MotionWrapper>
    </div>
  );
};

export default TabbedProductCard;
